import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import {
  GraduationCap,
  Loader2,
  Mail,
  MapPin,
  MessageCircle,
  Phone,
  Search,
} from "lucide-react";
import {
  useCreateHaafPayment,
  useHaafAlumniSearch,
  useHaafFinancialYears,
  useHaafMeta,
  useUpdateHaafPayment,
} from "@/api/haaf-payments";
import { currencies } from "@/lib/constants";
import { useDebounce } from "@/hooks/useDebounce";

const DEFAULT_PAYMENT_METHODS = [
  "Cash",
  "Bank Transfer",
  "UPI",
  "Card",
  "Cheque",
  "Other",
];

const DEFAULT_PAYMENT_AMOUNT = 1000;
const DEFAULT_PAYMENT_METHOD = "UPI";

function formatDateInput(date) {
  return date.toISOString().slice(0, 10);
}

function getFinancialYearId(year) {
  return year?.financialYearId || year?._id || year?.id || "";
}

function getAlumniProfileId(alumni) {
  return alumni?.profileId || alumni?._id || alumni?.id || "";
}

function getAlumniLine(alumni) {
  return [
    alumni.batchNumber ? `Batch ${alumni.batchNumber}` : null,
    alumni.currentChapter || alumni.permanentChapter,
    alumni.ugCollege,
  ]
    .filter(Boolean)
    .join(" | ");
}

function getAlumniEmail(alumni) {
  return alumni.email || alumni.emailAddress || alumni.email_id || "";
}

function getAlumniDetails(alumni) {
  return [
    {
      icon: Mail,
      label: "Email",
      value: getAlumniEmail(alumni),
      wide: true,
      wrap: true,
    },
    {
      icon: Phone,
      label: "Phone",
      value: alumni.phone || alumni.phoneNumber,
    },
    {
      icon: MessageCircle,
      label: "WhatsApp",
      value: alumni.whatsapp,
    },
    {
      icon: MapPin,
      label: "Current",
      value: alumni.currentChapter,
    },
    {
      icon: MapPin,
      label: "Permanent",
      value: alumni.permanentChapter,
    },
    {
      icon: GraduationCap,
      label: "College",
      value: alumni.ugCollege,
      wide: true,
      wrap: true,
    },
  ].filter((detail) => detail.value);
}

function renderAlumniDetails(alumni) {
  const details = getAlumniDetails(alumni);
  if (!details.length) return null;

  return (
    <div className="grid grid-cols-1 gap-1.5 text-xs text-muted-foreground sm:grid-cols-2">
      {details.map(({ icon: Icon, label, value, wide, wrap }) => (
        <div
          key={`${label}-${value}`}
          className={`flex min-w-0 items-start gap-1.5 ${
            wide ? "sm:col-span-2" : ""
          }`}
        >
          <Icon className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          <span className="shrink-0 font-medium text-foreground/70">
            {label}:
          </span>
          <span className={wrap ? "min-w-0 break-words" : "min-w-0 truncate"}>
            {value}
          </span>
        </div>
      ))}
    </div>
  );
}

function renderAlumniHeader(alumni) {
  return (
    <div className="flex min-w-0 flex-wrap items-center gap-1.5">
      <span className="min-w-0 break-words text-sm font-semibold text-foreground">
        {alumni.name}
      </span>
      {alumni.batchNumber && (
        <Badge variant="secondary" className="h-5 px-1.5 text-[11px]">
          Batch {alumni.batchNumber}
        </Badge>
      )}
      {(alumni.currentChapter || alumni.permanentChapter) && (
        <Badge variant="outline" className="h-5 px-1.5 text-[11px]">
          {alumni.currentChapter || alumni.permanentChapter}
        </Badge>
      )}
    </div>
  );
}

export function AddHaafPaymentForm({
  initialData,
  financialYearId,
  onSuccess,
}) {
  const isEditMode = !!initialData;
  const createMutation = useCreateHaafPayment();
  const updateMutation = useUpdateHaafPayment();
  const { data: meta, isLoading: isMetaLoading } = useHaafMeta();
  const { data: financialYears, isLoading: isYearsLoading } =
    useHaafFinancialYears();
  const [alumniSearch, setAlumniSearch] = useState(initialData?.payee || "");
  const [selectedAlumni, setSelectedAlumni] = useState(
    initialData
      ? {
          profileId: initialData.alumniProfileId,
          name: initialData.payee,
          batchNumber: initialData.batch,
          phone: initialData.phoneNumber,
          currentChapter: initialData.chapter?.chapterName,
        }
      : null,
  );
  const [isSubmitting, setIsSubmitting] = useState(false);
  const debouncedAlumniSearch = useDebounce(alumniSearch, 300);
  const { data: alumniResults, isFetching: isSearchingAlumni } =
    useHaafAlumniSearch(debouncedAlumniSearch);

  const form = useForm({
    defaultValues: {
      financialYearId: "",
      alumniProfileId: initialData?.alumniProfileId || "",
      amount: initialData?.amount || (isEditMode ? "" : DEFAULT_PAYMENT_AMOUNT),
      currency: initialData?.currency || "INR",
      paymentDate:
        initialData?.paymentDate?.slice?.(0, 10) || formatDateInput(new Date()),
      paymentMethod: initialData?.paymentMethod || DEFAULT_PAYMENT_METHOD,
      referenceNumber: initialData?.referenceNumber || "",
      notes: initialData?.notes || initialData?.remarks || "",
      isVerified: initialData?.verified ?? true,
    },
  });

  const activeFinancialYearId = useMemo(() => {
    const years = financialYears || [];
    const activeYear =
      years.find((year) => year.isActive) ||
      years
        .slice()
        .sort((a, b) => Number(b.startYear || 0) - Number(a.startYear || 0))[0];

    return getFinancialYearId(activeYear);
  }, [financialYears]);

  useEffect(() => {
    const targetFinancialYearId = financialYearId || activeFinancialYearId;
    if (!form.getValues("financialYearId") && targetFinancialYearId) {
      form.setValue("financialYearId", targetFinancialYearId);
    }
  }, [activeFinancialYearId, financialYearId, form]);

  const paymentMethods =
    meta?.paymentMethods?.length > 0
      ? meta.paymentMethods
      : DEFAULT_PAYMENT_METHODS;

  const supportedCurrencies = meta?.currencies?.length
    ? meta.currencies
    : Object.keys(currencies);

  const onSelectAlumni = (alumni) => {
    const profileId = getAlumniProfileId(alumni);
    setSelectedAlumni(alumni);
    form.setValue("alumniProfileId", profileId, {
      shouldValidate: true,
    });
    setAlumniSearch(alumni.name || "");
  };

  const onSubmit = async (data) => {
    try {
      setIsSubmitting(true);
      if (isEditMode) {
        await updateMutation.mutateAsync({
          id: initialData._id || initialData.paymentId,
          alumniProfileId: data.alumniProfileId || undefined,
          amount: String(data.amount),
          currency: data.currency,
          paymentDate: data.paymentDate,
          paymentMethod: data.paymentMethod,
          referenceNumber: data.referenceNumber,
          notes: data.notes,
        });
      } else {
        await createMutation.mutateAsync({
          ...data,
          financialYearId: financialYearId || data.financialYearId,
          amount: String(data.amount),
          isVerified: Boolean(data.isVerified),
        });
      }
      form.reset({
        financialYearId: financialYearId || activeFinancialYearId || "",
        alumniProfileId: "",
        amount: DEFAULT_PAYMENT_AMOUNT,
        currency: "INR",
        paymentDate: formatDateInput(new Date()),
        paymentMethod: DEFAULT_PAYMENT_METHOD,
        referenceNumber: "",
        notes: "",
        isVerified: true,
      });
      setSelectedAlumni(null);
      setAlumniSearch("");
      onSuccess?.();
    } catch (error) {
      console.error("Form submission error:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const isLoading =
    createMutation.isPending ||
    createMutation.isLoading ||
    updateMutation.isPending ||
    updateMutation.isLoading ||
    isMetaLoading ||
    isYearsLoading ||
    isSubmitting;

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
        {!financialYearId && !isEditMode && (
          <FormField
            control={form.control}
            name="financialYearId"
            rules={{ required: "Financial year is required" }}
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-sm">Financial Year</FormLabel>
                <Select onValueChange={field.onChange} value={field.value}>
                  <FormControl>
                    <SelectTrigger className="h-9">
                      <SelectValue placeholder="Select financial year" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {financialYears
                      ?.map((year) => ({
                        ...year,
                        optionId: getFinancialYearId(year),
                      }))
                      .filter((year) => year.optionId)
                      .map((year) => (
                        <SelectItem key={year.optionId} value={year.optionId}>
                          {year.name || `${year.startYear}-${year.endYear}`}{" "}
                          {year.isActive ? "(Active)" : ""}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />
        )}

        <FormField
          control={form.control}
          name="alumniProfileId"
          rules={{ required: "Select an alumni profile" }}
          render={() => (
            <FormItem>
              <FormLabel className="text-sm">Alumni</FormLabel>
              <FormControl>
                <div className="space-y-2">
                  <div className="relative">
                    <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                    <Input
                      value={alumniSearch}
                      onChange={(event) => {
                        setAlumniSearch(event.target.value);
                        setSelectedAlumni(null);
                        form.setValue("alumniProfileId", "");
                      }}
                      className="pl-8"
                      placeholder="Search name, batch, phone, chapter or college"
                    />
                  </div>

                  {selectedAlumni && (
                    <div className="rounded-md border bg-muted/30 p-3">
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0 flex-1 space-y-2">
                          {renderAlumniHeader(selectedAlumni)}
                          {renderAlumniDetails(selectedAlumni) || (
                            <p className="text-xs text-muted-foreground">
                              {getAlumniLine(selectedAlumni)}
                            </p>
                          )}
                        </div>
                        <Badge variant="outline" className="shrink-0">
                          Selected
                        </Badge>
                      </div>
                    </div>
                  )}

                  {!selectedAlumni && debouncedAlumniSearch?.length >= 2 && (
                    <div className="max-h-80 overflow-y-auto rounded-md border">
                      {isSearchingAlumni ? (
                        <div className="flex items-center gap-2 p-3 text-sm text-muted-foreground">
                          <Loader2 className="h-4 w-4 animate-spin" />
                          Searching alumni...
                        </div>
                      ) : alumniResults?.length ? (
                        alumniResults.map((alumni) => (
                          <button
                            type="button"
                            key={getAlumniProfileId(alumni)}
                            className="block w-full border-b p-3 text-left last:border-b-0 hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
                            onClick={() => onSelectAlumni(alumni)}
                          >
                            <div className="space-y-2">
                              {renderAlumniHeader(alumni)}
                              {renderAlumniDetails(alumni)}
                              {!getAlumniDetails(alumni).length && (
                                <p className="text-xs text-muted-foreground">
                                  {getAlumniLine(alumni)}
                                </p>
                              )}
                            </div>
                          </button>
                        ))
                      ) : (
                        <p className="p-3 text-sm text-muted-foreground">
                          No alumni found
                        </p>
                      )}
                    </div>
                  )}
                </div>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <FormField
            control={form.control}
            name="amount"
            rules={{
              required: "Amount is required",
              min: { value: 1, message: "Amount must be greater than zero" },
            }}
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-sm">Amount</FormLabel>
                <FormControl>
                  <Input
                    placeholder="Enter amount"
                    type="number"
                    step="0.01"
                    min="0"
                    {...field}
                    className="h-9"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="currency"
            rules={{ required: "Currency is required" }}
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-sm">Currency</FormLabel>
                <Select onValueChange={field.onChange} value={field.value}>
                  <FormControl>
                    <SelectTrigger className="h-9">
                      <SelectValue placeholder="Select currency" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {supportedCurrencies.map((code) => (
                      <SelectItem key={code} value={code}>
                        {currencies[code]?.symbol || code} {code} -{" "}
                        {currencies[code]?.name || code}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <FormField
            control={form.control}
            name="paymentDate"
            rules={{ required: "Payment date is required" }}
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-sm">Payment Date</FormLabel>
                <FormControl>
                  <Input type="date" {...field} className="h-9" />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="paymentMethod"
            rules={{ required: "Payment method is required" }}
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-sm">Payment Method</FormLabel>
                <Select onValueChange={field.onChange} value={field.value}>
                  <FormControl>
                    <SelectTrigger className="h-9">
                      <SelectValue placeholder="Select method" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {paymentMethods.map((method) => (
                      <SelectItem key={method} value={method}>
                        {method}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <FormField
          control={form.control}
          name="referenceNumber"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="text-sm">Reference Number</FormLabel>
              <FormControl>
                <Input
                  placeholder="Transaction/reference number (optional)"
                  {...field}
                  className="h-9"
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="notes"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="text-sm">Notes</FormLabel>
              <FormControl>
                <Textarea
                  placeholder="Additional information (optional)"
                  className="h-20 resize-none"
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        {!isEditMode && (
          <FormField
            control={form.control}
            name="isVerified"
            render={({ field }) => (
              <FormItem className="rounded-md border bg-muted/30 p-3">
                <div className="flex items-start gap-3">
                  <FormControl>
                    <Checkbox
                      checked={Boolean(field.value)}
                      onCheckedChange={(checked) =>
                        field.onChange(checked === true)
                      }
                    />
                  </FormControl>
                  <div className="space-y-0.5">
                    <FormLabel className="text-sm font-medium">
                      Verify this payment immediately
                    </FormLabel>
                    <p className="text-xs text-muted-foreground">
                      Creates the HAF payment as verified using the HAF API.
                    </p>
                  </div>
                </div>
              </FormItem>
            )}
          />
        )}

        <Button
          type="submit"
          className="h-9 w-full bg-blue-600 font-medium text-white hover:bg-blue-700"
          disabled={isLoading}
        >
          {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          {isEditMode ? "Update HAF Payment" : "Add HAF Payment"}
        </Button>
      </form>
    </Form>
  );
}
